/**
 * My Meetings' two lists (TMMAPP-0103): Upcoming is now to 12 months ahead, ascending; Past is
 * 12 months back to now, descending. Both come from `listMine({ from, to })`, which windows by
 * civil date in the viewer's zone; the split inside today is by instant.
 */
import type { MeetingCalendarEntry } from '@/api/meetings';
import { addCivilMonths } from '@/shared/servedWindow';

export function upcomingRange(today: string): { from: string; to: string } {
  return { from: today, to: addCivilMonths(today, 12) };
}

export function pastRange(today: string): { from: string; to: string } {
  return { from: addCivilMonths(today, -12), to: today };
}

/** The entry's instant of record: when it ends. Null when it isn't a parseable instant. */
function endMs(entry: MeetingCalendarEntry): number | null {
  const ms = Date.parse(entry.endsAt);
  return Number.isFinite(ms) ? ms : null;
}

function startMs(entry: MeetingCalendarEntry): number {
  const ms = Date.parse(entry.startsAt);
  return Number.isFinite(ms) ? ms : 0;
}

/** An occurrence has happened when it ended before `now` and isn't live. */
export function isPastEntry(entry: MeetingCalendarEntry, now: number): boolean {
  if (entry.live) return false;
  const end = endMs(entry);
  return end !== null && end < now;
}

export function splitMeetings(
  entries: readonly MeetingCalendarEntry[],
  now: number,
): { upcoming: MeetingCalendarEntry[]; past: MeetingCalendarEntry[] } {
  const seen = new Set<string>();
  const unique = entries.filter((entry) => {
    const key = `${entry.meetingId}:${entry.occurrenceKey}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  return {
    upcoming: unique.filter((e) => !isPastEntry(e, now)).sort((a, b) => startMs(a) - startMs(b)),
    past: unique.filter((e) => isPastEntry(e, now)).sort((a, b) => startMs(b) - startMs(a)),
  };
}

/** The current instant in ms. A function of its own so a render path and an event handler read the clock the same way. */
export const nowMs = (): number => new Date().getTime();
